"use client";

import { useRef, useEffect, useState, useCallback, useId } from "react";
import Image from "next/image";
import { cn } from "@repo/ui/utils.ts";
import { Overline } from "@repo/ui/overline";
import { RichTextCondensed } from "@/components/common/rich-text/rich-text-condensed.component";
import type { LogoStripProps, LogoItem } from "./logo-strip.types";

const CARD_WIDTH = 142;
const GAP = 24;

function LogoCard({ logo }: { logo: LogoItem }) {
  if (!logo.url) return null;

  return (
    <div className="flex h-[80px] w-[142px] shrink-0 items-center justify-center rounded-xl bg-surface-base-secondary p-[18px]">
      <Image
        src={logo.url}
        alt={logo.alt || logo.name || "Partner logo"}
        width={logo.width || 106}
        height={logo.height || 44}
        className="h-full w-auto object-contain"
        placeholder={logo.lqip ? "blur" : "empty"}
        blurDataURL={logo.lqip || undefined}
      />
    </div>
  );
}

function MarqueeRow({
  logos,
  reverse = false,
  duration = 50,
  staggerOffset = 0,
}: {
  logos: LogoItem[];
  reverse?: boolean;
  duration?: number;
  /** Initial horizontal offset in pixels to stagger rows */
  staggerOffset?: number;
}) {
  const id = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const [repeatCount, setRepeatCount] = useState(2);
  const [oneSetWidth, setOneSetWidth] = useState(0);

  const measure = useCallback(() => {
    if (!containerRef.current) return;
    const viewportWidth = containerRef.current.offsetWidth;
    const setWidth = logos.length * CARD_WIDTH + (logos.length - 1) * GAP;
    // Enough copies to fill viewport + one full set + stagger offset
    const totalNeeded = viewportWidth + setWidth + Math.abs(staggerOffset);
    const minCopies = Math.max(2, Math.ceil(totalNeeded / setWidth) + 1);
    setRepeatCount(minCopies);
    setOneSetWidth(setWidth + GAP);
  }, [logos.length, staggerOffset]);

  useEffect(() => {
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [measure]);

  if (!logos.length) return null;

  const animName = `marquee-${id.replace(/:/g, "")}`;
  const initialOffset = reverse
    ? -(oneSetWidth + staggerOffset)
    : -staggerOffset;
  const endOffset = reverse ? -staggerOffset : -(oneSetWidth + staggerOffset);

  return (
    <div ref={containerRef} className="overflow-hidden" aria-hidden="true">
      <div
        className="flex gap-6 will-change-transform"
        style={
          oneSetWidth > 0
            ? { animation: `${animName} ${duration}s linear infinite` }
            : undefined
        }
      >
        {Array.from({ length: repeatCount }, (_, copyIndex) =>
          logos.map((logo) => (
            <LogoCard key={`${copyIndex}-${logo._key}`} logo={logo} />
          )),
        )}
      </div>
      {oneSetWidth > 0 && (
        <style>{`
          @keyframes ${animName} {
            from { transform: translateX(${initialOffset}px); }
            to { transform: translateX(${endOffset}px); }
          }
          @media (prefers-reduced-motion: reduce) {
            .${animName} { animation: none !important; }
          }
        `}</style>
      )}
    </div>
  );
}

export function LogoStrip({
  overline,
  headline,
  body,
  logos,
  className,
}: LogoStripProps) {
  const hasHeader = overline || headline || body;
  const hasLogos = logos && logos.length > 0;

  if (!hasLogos) return null;

  return (
    <section
      className={cn("bg-surface-base-primary", className)}
      data-testid="logo-strip"
      aria-label={headline || "Partner logos"}
    >
      {/* Header */}
      {hasHeader && (
        <div className="container-grid pt-12 desktop:py-20">
          <div className="col-span-full grid grid-cols-subgrid gap-y-6 desktop:gap-y-0">
            <div className="col-span-full desktop:col-span-5 flex flex-col gap-4 desktop:gap-6">
              {overline && <Overline>{overline}</Overline>}
              {headline && (
                <h2 className="typography-mobile-heading-h5 desktop:typography-desktop-heading-h5 text-text-primary">
                  {headline}
                </h2>
              )}
            </div>

            {body && (
              <div className="col-span-full desktop:col-span-4 desktop:col-start-9 flex items-start">
                <RichTextCondensed
                  richText={body}
                  className="text-text-secondary"
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Accessible logo list for screen readers */}
      <ul className="sr-only">
        {logos.map((logo) => (
          <li key={logo._key}>{logo.alt || logo.name || "Partner logo"}</li>
        ))}
      </ul>

      {/* Logo rows — full bleed, both rows show all logos */}
      <div className="overflow-clip pt-8 pb-16 desktop:pb-48">
        <div className="flex flex-col gap-8">
          <MarqueeRow logos={logos} />
          <MarqueeRow logos={logos} reverse staggerOffset={83} />
        </div>
      </div>
    </section>
  );
}
